import {
  CalendarOutlined,
  CheckCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  HolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, DatePicker, Dropdown, Input, Radio, Select, Switch, Table } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useGetAttributesQuery } from '@/modules/attributes/attributesApi';
import {
  useBulkDeleteAttributeValuesMutation,
  useBulkUpdateAttributeValueStatusMutation,
  useDeleteAttributeValueMutation,
  useGetAttributeValuesQuery,
  useReorderAttributeValuesMutation,
  useUpdateAttributeValueMutation,
} from '@/modules/attribute-values/attributeValuesApi';
import type { AttributeValue, AttributeValueStatusFilter } from '@/types/attribute-value';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { getAttributeValueImageUrl } from '@/utils/media';
import { reorderById } from '@/utils/reorder';
import { isImprintAttribute } from '@/utils/attribute';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { AttributeValuesTableSkeleton } from './components/AttributeValuesTableSkeleton';
import { AttributeValueFormModal } from './components/AttributeValueFormModal';
import { useAttributeValueQueryParams } from './hooks/useAttributeValueQueryParams';
import styles from './AttributeValues.module.scss';

export default function AttributeValues() {
  const notify = useNotify();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } = useAttributeValueQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetAttributeValuesQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const { data: attributesResult } = useGetAttributesQuery({
    page: 1,
    limit: 100,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const attributes = useMemo(() => attributesResult?.results ?? [], [attributesResult?.results]);

  const attributeValues = listResult?.results ?? [];
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const canDrag =
    state.sortBy === 'sortOrder' &&
    !state.search &&
    state.status === 'all' &&
    !state.dateFrom &&
    !state.dateTo;

  const { data: attributeSortListResult } = useGetAttributeValuesQuery(
    {
      attributeId: state.attributeId,
      page: 1,
      limit: 1000,
      sortBy: 'sortOrder',
      sortOrder: 'asc',
    },
    { skip: !canDrag || !state.attributeId },
  );

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [statusDraft, setStatusDraft] = useState<AttributeValueStatusFilter>(state.status);
  const [attributeDraft, setAttributeDraft] = useState(state.attributeId);
  const [dateDraft, setDateDraft] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    state.dateFrom || state.dateTo
      ? [state.dateFrom ? dayjs(state.dateFrom) : null, state.dateTo ? dayjs(state.dateTo) : null]
      : null,
  );

  const [updateAttributeValue] = useUpdateAttributeValueMutation();
  const [deleteAttributeValue] = useDeleteAttributeValueMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateAttributeValueStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteAttributeValuesMutation();
  const [reorderAttributeValues, { isLoading: isReordering }] = useReorderAttributeValuesMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAttributeValue, setEditingAttributeValue] = useState<AttributeValue | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const attributeMap = useMemo(
    () => new Map(attributes.map((attribute) => [attribute.id, attribute.name])),
    [attributes],
  );

  const imprintAttributeIds = useMemo(
    () => new Set(attributes.filter(isImprintAttribute).map((attribute) => attribute.id)),
    [attributes],
  );

  const isImprintValue = useCallback(
    (attributeId: string) => imprintAttributeIds.has(attributeId),
    [imprintAttributeIds],
  );

  const attributeFilterOptions = useMemo(
    () => attributes.map((attribute) => ({ label: attribute.name, value: attribute.id })),
    [attributes],
  );

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const openCreate = () => {
    setEditingAttributeValue(null);
    setModalOpen(true);
  };

  const openEdit = useCallback((attributeValue: AttributeValue) => {
    setEditingAttributeValue(attributeValue);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingAttributeValue(null);
  };

  const clearSelection = () => setSelectedRowKeys([]);

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setStatusDraft('all');
    setAttributeDraft('');
    setDateDraft(null);
    resetAll();
  };

  const handleStatusChange = useCallback(
    async (attributeValue: AttributeValue, isActive: boolean) => {
      const result = await resolveMutationResult(
        updateAttributeValue({ id: attributeValue.id, body: { isActive } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateAttributeValue],
  );

  const handleDelete = useCallback(
    async (attributeValue: AttributeValue) => {
      const confirmed = await confirmDelete(attributeValue.name);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteAttributeValue(attributeValue.id));
      if (result.ok) {
        notify.success(result.message ?? 'AttributeValue deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== attributeValue.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteAttributeValue, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (selectedRowKeys.length === 0) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'AttributeValues updated.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));
    if (result.ok) {
      notify.success(result.message ?? 'Attribute values deleted.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleDrop = async (dropId: string) => {
    if (!canDrag || !dragId || dragId === dropId) {
      setDragId(null);
      return;
    }

    const dragged = attributeValues.find((item) => item.id === dragId);
    const dropped = attributeValues.find((item) => item.id === dropId);

    if (!dragged || !dropped || dragged.attributeId !== dropped.attributeId) {
      notify.error('You can only reorder attribute values within the same attribute.');
      setDragId(null);
      return;
    }

    const scope = state.attributeId
      ? (attributeSortListResult?.results ?? attributeValues)
      : attributeValues.filter((item) => item.attributeId === dragged.attributeId);
    const reordered = reorderById(scope, dragId, dropId);
    setDragId(null);

    const result = await resolveMutationResult(
      reorderAttributeValues({ ids: reordered.map((item) => item.id) }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Order updated.');
      return;
    }

    notify.error(result.message);
  };

  const handleTableChange: TableProps<AttributeValue>['onChange'] = (pagination) => {
    const pageChanged =
      pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged =
      pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (attributeValue: AttributeValue): MenuProps['items'] => [
      { key: 'edit', label: 'Edit', onClick: () => openEdit(attributeValue) },
      { type: 'divider' },
      { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDelete(attributeValue) },
    ],
    [handleDelete, openEdit],
  );

  const columns: ColumnsType<AttributeValue> = useMemo(
    () => [
      {
        title: '',
        key: 'drag',
        width: 40,
        className: styles.dragCol,
        render: () => (canDrag ? <HolderOutlined className={styles.dragHandle} /> : null),
      },
      {
        title: 'IMAGE',
        dataIndex: 'image',
        key: 'image',
        width: 72,
        className: styles.imageCol,
        render: (image: string | undefined, record) =>
          isImprintValue(record.attributeId) ? (
            <PreviewImage
              src={getAttributeValueImageUrl(image)}
              alt=""
              size={44}
              shape="square"
              className={styles.thumb}
            />
          ) : (
            '—'
          ),
      },
      {
        title: (
          <SortableColumnTitle
            label="TITLE"
            field="name"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'name',
        key: 'name',
        className: styles.nameCol,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.search ? [state.search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name or slug"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(state.search);
          },
        },
        render: (name: string, record) => (
          <div className={styles.nameBlock}>
            <div className={styles.nameRow}>
              {record.colorCode ? (
                <span
                  className={styles.colorSwatch}
                  style={{ backgroundColor: record.colorCode }}
                  title={record.colorCode}
                />
              ) : null}
              <span className={styles.nameCell}>{ucfirst(name)}</span>
            </div>
            {record.subtitle && isImprintValue(record.attributeId) ? (
              <span className={styles.subtitle}>{record.subtitle}</span>
            ) : null}
            <span className={styles.slug}>{record.slug}</span>
          </div>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="ATTRIBUTE"
            field="attributeId"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'attributeId',
        key: 'attributeId',
        className: styles.categoryCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.attributeId ? [state.attributeId] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ attributeId: attributeDraft || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setAttributeDraft('');
              patchParams({ attributeId: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              allowClear
              showSearch
              size="small"
              className={styles.categorySelect}
              placeholder="All attributes"
              optionFilterProp="label"
              options={attributeFilterOptions}
              value={attributeDraft || undefined}
              onChange={(value) => setAttributeDraft(value ?? '')}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setAttributeDraft(state.attributeId);
          },
        },
        render: (attributeId: string) => (
          <span className={styles.categoryCell}>{ucfirst(attributeMap.get(attributeId)) || '—'}</span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ status: statusDraft === 'all' ? null : statusDraft }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            onChange={(checked) => handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="SORT"
            field="sortOrder"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        align: 'center',
        render: (value: number) => <span className={styles.sortNum}>{value + 1}</span>,
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        filterIcon: (filtered) => (
          <CalendarOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.dateFrom || state.dateTo ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                {
                  dateFrom: dateDraft?.[0]?.format('YYYY-MM-DD') || null,
                  dateTo: dateDraft?.[1]?.format('YYYY-MM-DD') || null,
                },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setDateDraft(null);
              patchParams({ dateFrom: null, dateTo: null }, { resetPage: true });
              close();
            }}
          >
            <DatePicker.RangePicker
              size="small"
              value={dateDraft}
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => setDateDraft(values)}
              style={{ width: '100%' }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setDateDraft(
                state.dateFrom || state.dateTo
                  ? [
                      state.dateFrom ? dayjs(state.dateFrom) : null,
                      state.dateTo ? dayjs(state.dateTo) : null,
                    ]
                  : null,
              );
            }
          },
        },
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']} placement="bottomRight">
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              icon={<EllipsisOutlined />}
              aria-label="Open actions"
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [
      handleSort,
      activeSortBy,
      activeSortOrder,
      state.search,
      state.status,
      state.attributeId,
      state.dateFrom,
      state.dateTo,
      canDrag,
      searchDraft,
      patchParams,
      statusDraft,
      attributeDraft,
      attributeFilterOptions,
      attributeMap,
      isImprintValue,
      handleStatusChange,
      dateDraft,
      getRowActionItems,
    ],
  );

  const rowSelection: TableRowSelection<AttributeValue> = {
    columnWidth: 40,
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  const showSkeleton = isLoading && !listResult;
  const mutationLoading = isReordering || isBulkUpdating || isBulkDeleting;
  const hasSelection = selectedRowKeys.length > 0;

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFiltersAndSort}>
                Reset
              </Button>
            ) : null}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add sub attribute
            </Button>
          </>
        }
      />

      {hasSelection ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={handleBulkDelete} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        {showSkeleton ? (
          <AttributeValuesTableSkeleton rows={pageSize} />
        ) : (
          <Table<AttributeValue>
            className={styles.table}
            rowKey="id"
            size="small"
            loading={mutationLoading}
            columns={columns}
            dataSource={attributeValues}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
              showTotal: (total, [start, end]) => `${start}-${end} of ${total}`,
              size: 'small',
            }}
            onRow={(record) => ({
              draggable: canDrag,
              className: dragId === record.id ? styles.draggingRow : undefined,
              onDragStart: () => setDragId(record.id),
              onDragEnd: () => setDragId(null),
              onDragOver: (event) => event.preventDefault(),
              onDrop: () => handleDrop(record.id),
            })}
          />
        )}
      </div>

      <AttributeValueFormModal
        open={modalOpen}
        attributeValue={editingAttributeValue}
        defaultAttributeId={state.attributeId || undefined}
        onClose={closeModal}
      />
    </div>
  );
}
